const UNIT_FACTORS_TO_METERS = {
  m: 1,
  cm: 0.01,
  in: 0.0254,
  ft: 0.3048
};

export const SUPPORTED_UNITS = Object.freeze(Object.keys(UNIT_FACTORS_TO_METERS));

export function assertSupportedUnit(unit) {
  if (!UNIT_FACTORS_TO_METERS[unit]) {
    throw new Error(`Unsupported unit: ${unit}. Supported units: ${SUPPORTED_UNITS.join(", ")}.`);
  }
}

export function normalizeCoordinateSystem(coordinates = {}) {
  const unit = coordinates.unit ?? "m";
  const scale = Number(coordinates.scale ?? 1);
  const displayUnit = coordinates.displayUnit ?? unit;

  assertSupportedUnit(unit);
  assertSupportedUnit(displayUnit);
  assertPositiveNumber(scale, "Coordinate scale");

  return { unit, scale, displayUnit };
}

export function convertLength(value, fromUnit, toUnit) {
  assertSupportedUnit(fromUnit);
  assertSupportedUnit(toUnit);

  return (Number(value) * UNIT_FACTORS_TO_METERS[fromUnit]) / UNIT_FACTORS_TO_METERS[toUnit];
}

export function documentDistanceToUnit(distance, coordinates, targetUnit) {
  const normalizedCoordinates = normalizeCoordinateSystem(coordinates);
  const displayUnit = targetUnit ?? normalizedCoordinates.displayUnit;
  const realDistanceInDocumentUnit = Number(distance) * normalizedCoordinates.scale;

  return convertLength(realDistanceInDocumentUnit, normalizedCoordinates.unit, displayUnit);
}

export function unitLengthToDocument(length, unit, coordinates) {
  const normalizedCoordinates = normalizeCoordinateSystem(coordinates);
  const lengthInDocumentUnit = convertLength(Number(length), unit, normalizedCoordinates.unit);

  return lengthInDocumentUnit / normalizedCoordinates.scale;
}

export function convertSourceCoordinateValue(value, sourceCoordinates, targetCoordinates) {
  const source = normalizeCoordinateSystem(sourceCoordinates);
  const target = normalizeCoordinateSystem(targetCoordinates);
  const sourceLength = Number(value) * source.scale;

  return convertLength(sourceLength, source.unit, target.unit) / target.scale;
}

export function convertSourcePointToDocument(point, sourceCoordinates, targetCoordinates) {
  return {
    ...point,
    x: convertSourceCoordinateValue(point.x, sourceCoordinates, targetCoordinates),
    y: convertSourceCoordinateValue(point.y, sourceCoordinates, targetCoordinates)
  };
}

export function worldToScreen(point, viewport = {}) {
  const zoom = viewport.zoom ?? 1;
  const panX = viewport.panX ?? 0;
  const panY = viewport.panY ?? 0;

  return {
    x: Number(point.x) * zoom + panX,
    y: Number(point.y) * zoom + panY
  };
}

export function screenToWorld(point, viewport = {}) {
  const zoom = viewport.zoom ?? 1;
  const panX = viewport.panX ?? 0;
  const panY = viewport.panY ?? 0;

  assertPositiveNumber(zoom, "Viewport zoom");

  return {
    x: (Number(point.x) - panX) / zoom,
    y: (Number(point.y) - panY) / zoom
  };
}

export function lengthBetweenDots(start, end) {
  if (!start || !end) {
    return 0;
  }

  return Math.hypot(Number(end.x) - Number(start.x), Number(end.y) - Number(start.y));
}

export function getGridSpacingDocumentUnits(grid = {}, coordinates = {}) {
  const spacing = grid.spacing ?? 1;
  const unit = grid.unit ?? normalizeCoordinateSystem(coordinates).unit;

  assertPositiveNumber(spacing, "Grid spacing");
  return unitLengthToDocument(spacing, unit, coordinates);
}

export function getGridLineValues(min, max, spacing) {
  assertPositiveNumber(spacing, "Grid spacing");

  const values = [];
  const start = Math.floor(min / spacing) * spacing;
  const roundedStart = roundGridValue(start);

  for (let value = roundedStart; value <= max + spacing * 0.5; value += spacing) {
    values.push(roundGridValue(value));
  }

  return values;
}

export function normalizeViewport(viewport = {}) {
  const minZoom = Number(viewport.minZoom ?? 0.25);
  const maxZoom = Number(viewport.maxZoom ?? 4);
  const zoomStep = Number(viewport.zoomStep ?? 1.2);
  const panStep = Number(viewport.panStep ?? 48);

  assertPositiveNumber(minZoom, "Viewport minZoom");
  assertPositiveNumber(maxZoom, "Viewport maxZoom");
  assertPositiveNumber(zoomStep, "Viewport zoomStep");
  assertPositiveNumber(panStep, "Viewport panStep");

  if (minZoom > maxZoom) {
    throw new Error("Viewport minZoom must be less than or equal to maxZoom.");
  }

  const zoom = clampZoom(viewport.zoom ?? 1, minZoom, maxZoom);

  return {
    zoom,
    minZoom,
    maxZoom,
    panX: assertFiniteNumber(viewport.panX ?? 0, "Viewport panX"),
    panY: assertFiniteNumber(viewport.panY ?? 0, "Viewport panY"),
    zoomStep,
    panStep,
    wheelZoom: viewport.wheelZoom ?? true,
    keyboard: viewport.keyboard ?? true
  };
}

export function clampZoom(zoom, minZoom = 0.25, maxZoom = 4) {
  assertPositiveNumber(minZoom, "Viewport minZoom");
  assertPositiveNumber(maxZoom, "Viewport maxZoom");

  const numericZoom = assertFiniteNumber(zoom, "Viewport zoom");

  return Math.min(maxZoom, Math.max(minZoom, numericZoom));
}

export function validateUnitConfiguration(config = {}) {
  normalizeCoordinateSystem(config.coordinates);

  if (config.grid?.unit) {
    assertSupportedUnit(config.grid.unit);
  }

  if (config.grid?.spacing !== undefined) {
    assertPositiveNumber(config.grid.spacing, "Grid spacing");
  }

  if (config.measurements?.displayUnit) {
    assertSupportedUnit(config.measurements.displayUnit);
  }
}

function roundGridValue(value) {
  return Number(value.toFixed(8));
}

function assertPositiveNumber(value, label) {
  if (!Number.isFinite(Number(value)) || Number(value) <= 0) {
    throw new Error(`${label} must be a positive number.`);
  }
}

function assertFiniteNumber(value, label) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    throw new Error(`${label} must be a finite number.`);
  }

  return number;
}
